import React, { useState, useEffect, useCallback } from "react";
import { getSessionUser } from "../../../../utils/auth/sessionUser";
import dayjs from "dayjs";
import { Button, Space, Typography, Tooltip, Popconfirm, Avatar, Tag } from "antd";
import {
	CheckCircleOutlined,
	ExclamationCircleFilled,
	PauseCircleOutlined,
	PlayCircleOutlined,
	StopOutlined,
	UserOutlined,
} from "@ant-design/icons";

import {
	FlowNode,
	PatientTreatmentPlan,
	PatientTreatmentPlanStageLog,
} from "../../../../types/healthcare/TreatmentPlanTypes";
import {
	fetchPlanStageLog,
	pausePlan,
	resumePlan,
	cancelPlan,
} from "../../../../api/healthcare/TreatmentPlanApi";
import { AssignedForm, getAssignedForms } from "../../../../api/formApi";
import {
	buildWatcherChildMap,
	buildWatcherParentMap,
} from "./planDrawerComponents/workflowProgress/watcherMerge";
import { pickActiveLog } from "./planDrawerComponents/workflowProgress/attemptSelection";
import { fetchPatient } from "../../../../api/healthcare/PatientApi";
import { User } from "../../../../types/admin/UserTypes";
import { useNotifications } from "../../../../context/NotificationContext";

import PatientMetaPanel from "./planDrawerComponents/PatientMetaPanel";
import DrawerStageList from "./planDrawerComponents/DrawerStageList";
import InlineStageRenderer from "./planDrawerComponents/InlineStageRenderer";
import StatusTag from "../../../../components/shared/StatusTag";
import { DetailDrawer, ContrastTag, BetaNotice } from "../../../../components/shared";
import ReviewFormModal from "../../formsInTray/ReviewFormModal";
import type { UserUuid } from "../../../../types/identity";

const { Text } = Typography;

interface PlanDetailDrawerProps {
	isConsult?: boolean;
	plan: PatientTreatmentPlan | null;
	onClose: () => void;
	onPlanChange?: () => void;
	showToolbar?: boolean;
	/** Stage log to preselect when the drawer opens (e.g. the task a user clicked "View" on). */
	initialSelectedStageLogId?: number | null;
}

export const PlanDetailDrawer: React.FC<PlanDetailDrawerProps> = ({
	isConsult = false,
	plan,
	onClose,
	onPlanChange,
	showToolbar = false,
	initialSelectedStageLogId = null,
}) => {
	const user = getSessionUser() as User;
	const { showSuccess, showError } = useNotifications();

	const [stageLogs, setStageLogs] = useState<PatientTreatmentPlanStageLog[]>([]);
	const [loading, setLoading] = useState(false);
	const [selectedStageLogId, setSelectedStageLogId] = useState<number | null>(null);
	const [viewAssignedForm, setViewAssignedForm] = useState<AssignedForm | null>(null);
	const [fetchedPatient, setFetchedPatient] = useState<any>(null);

	const loadStageLogs = useCallback(async (planId: number) => {
		setLoading(true);
		try {
			const logs = await fetchPlanStageLog(planId);
			setStageLogs(logs);
		} catch {
			showError("Failed to load stage history");
		} finally {
			setLoading(false);
		}
	}, []);

	// Reset selected stage when plan changes — seed with the caller's target stage if provided.
	useEffect(() => {
		setSelectedStageLogId(initialSelectedStageLogId ?? null);
		setFetchedPatient(null);
	}, [plan?.id, initialSelectedStageLogId]);

	// Fetch patient if not embedded in the plan response
	useEffect(() => {
		if (!plan) return;
		if ((plan as any).patient) return;
		if (!plan.patient_id) return;
		fetchPatient(String(plan.patient_id))
			.then((res) => setFetchedPatient(res.data))
			.catch(() => {});
	}, [plan?.id, plan?.patient_id]);

	useEffect(() => {
		if (plan?.id) loadStageLogs(plan.id);
	}, [plan?.id, plan?.status]);

	const handleStageReviewed = useCallback(() => {
		onPlanChange?.();
		if (plan?.id) loadStageLogs(plan.id);
	}, [plan?.id, onPlanChange, loadStageLogs]);

	if (!plan) return null;

	const patientObj = (plan as any).patient ?? fetchedPatient;
	// Provide a plan view with patient populated for child components that read plan.patient
	const planWithPatient = patientObj && !(plan as any).patient
		? { ...plan, patient: patientObj }
		: plan;
	const patientId: UserUuid = patientObj?.id ?? plan.patient_id;
	const patientUuid: string = patientObj?.uuid ?? "";
	const isEditable = plan.status === "active" || plan.status === "paused";

	// Derive the active stage log — user selection takes priority, else the CURRENT (highest-iteration)
	// non-terminal attempt so a fresh loop-back lands on the live attempt, not a prior completed one.
	const baseActiveLog: PatientTreatmentPlanStageLog | undefined =
		selectedStageLogId !== null
			? stageLogs.find((l) => l.id === selectedStageLogId)
			: pickActiveLog(stageLogs);

	// ── Watcher visual merge (frontend only) ─────────────────────────────────
	// A watcher is its own backend stage, but in this modal it is presented as part of the stage it
	// watches. Map watcher↔parent from the canvas, then: if the active step is a merged watcher,
	// show the stage it's joined to as the primary view and surface the watcher's state inside it;
	// if the active step is a stage that has a watcher child, attach that watcher's log too.
	const canvasData = plan.template?.canvas_data ?? null;
	const nodeByKey = (key?: string): FlowNode | undefined =>
		key ? canvasData?.nodes?.find((n) => n.id === key) : undefined;
	const logByStageKey = new Map<string, PatientTreatmentPlanStageLog>();
	// Iteration-aware lookup (stage_key + loop_iteration) so a watcher/parent log is matched to the
	// CURRENT attempt — not a prior loop iteration's log that happens to share the same stage_key.
	const logByStageKeyIter = new Map<string, PatientTreatmentPlanStageLog>();
	stageLogs.forEach((l) => {
		const sk = l.stage_snapshot?.stage_key ?? l.stage?.stage_key;
		if (sk) {
			logByStageKey.set(sk, l);
			logByStageKeyIter.set(`${sk}::${l.loop_iteration ?? 0}`, l);
		}
	});
	const logForKeyAtIteration = (
		key: string | undefined,
		iteration: number,
	): PatientTreatmentPlanStageLog | undefined =>
		key ? logByStageKeyIter.get(`${key}::${iteration}`) : undefined;
	const watcherParent = buildWatcherParentMap(canvasData);
	const watcherChildByParent = buildWatcherChildMap(canvasData);

	let activeStageLog = baseActiveLog;
	let joinedWatcherLog: PatientTreatmentPlanStageLog | null = null;
	let joinedWatcherNode: FlowNode | undefined;

	if (baseActiveLog) {
		const activeKey =
			baseActiveLog.stage_snapshot?.stage_key ?? baseActiveLog.stage?.stage_key;
		const activeIter = baseActiveLog.loop_iteration ?? 0;
		if (activeKey && watcherParent.has(activeKey)) {
			// Active step IS a merged watcher → render its parent stage (same attempt) as primary.
			const parentKey = watcherParent.get(activeKey);
			const parentLog =
				logForKeyAtIteration(parentKey, activeIter) ??
				(parentKey ? logByStageKey.get(parentKey) : undefined);
			joinedWatcherLog = baseActiveLog;
			joinedWatcherNode = nodeByKey(activeKey);
			if (parentLog) activeStageLog = parentLog;
		} else if (activeKey && watcherChildByParent.has(activeKey)) {
			// Active step has a watcher child → attach it ONLY if a log exists for THIS attempt's
			// iteration. A fresh re-upload (loop-back) attempt has no watcher log yet, so we must NOT
			// short-circuit to the prior attempt's CoA-analysis view — the upload form must render.
			const watcherKey = watcherChildByParent.get(activeKey);
			joinedWatcherNode = nodeByKey(watcherKey);
			joinedWatcherLog = logForKeyAtIteration(watcherKey, activeIter) ?? null;
		}
	}

	// Derive the matching canvas node for the (possibly remapped) active log
	const activeNode = activeStageLog
		? plan.template?.canvas_data?.nodes?.find((n) => {
				const sk =
					activeStageLog!.stage_snapshot?.stage_key ?? activeStageLog!.stage?.stage_key;
				return n.id === sk;
		  })
		: undefined;

	// Open a patient's submitted form (read-only) from a form_completed watcher.
	const handleViewWatcherForm = async (formId: number) => {
		try {
			const forms = await getAssignedForms(patientId);
			const match =
				forms.find(
					(f) =>
						f.form_id === formId &&
						["completed", "reviewed"].includes((f.status ?? "").toLowerCase()),
				) ?? forms.find((f) => f.form_id === formId);
			if (match) {
				setViewAssignedForm(match);
			} else {
				showError("Submitted form could not be found for this patient.");
			}
		} catch {
			showError("Failed to load the submitted form.");
		}
	};

	const tagStyle: React.CSSProperties = {
		fontSize: 11,
		padding: "0 6px",
		margin: 0,
		color: "var(--mg-text-secondary)",
		background: "var(--mg-bg-subtle)",
		border: "1px solid var(--mg-border)",
	};

	const patientAge = patientObj?.date_of_birth
		? dayjs().diff(dayjs(patientObj.date_of_birth), "year") + " yrs"
		: null;

	/* ─── Handlers ───────────────────────────────────────────────────────── */

	const handlePause = async () => {
		try {
			await pausePlan(plan.id);
			showSuccess("Plan paused");
			onPlanChange?.();
			loadStageLogs(plan.id);
		} catch {
			showError("Failed to pause plan");
		}
	};

	const handleResume = async () => {
		try {
			await resumePlan(plan.id);
			showSuccess("Plan resumed");
			onPlanChange?.();
			loadStageLogs(plan.id);
		} catch {
			showError("Failed to resume plan");
		}
	};

	const handleCancel = async () => {
		try {
			await cancelPlan(plan.id);
			showSuccess("Plan cancelled");
			onPlanChange?.();
			onClose();
		} catch {
			showError("Failed to cancel plan");
		}
	};


	/* ─── Idle/completion state for Col 3 ───────────────────────────────── */
	const idleContent = (
		<div
			style={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "center",
				flex: 1,
				gap: 8,
				color: "var(--mg-text-secondary)",
				padding: 24,
			}}
		>
			<CheckCircleOutlined
				style={{
					fontSize: 32,
					color: plan.status === "completed" ? "var(--mg-success)" : "var(--mg-text-disabled)",
				}}
			/>
			<Text
				type="secondary"
				style={{ fontSize: 13, textAlign: "center" }}
			>
				{plan.status === "completed"
					? "Plan completed"
					: plan.status === "paused"
					? "Plan paused — resume to continue"
					: plan.status === "cancelled"
					? "Plan cancelled"
					: "No active stage"}
			</Text>
		</div>
	);

	/* ─── 3-column body ──────────────────────────────────────────────────── */
	const threeColContent = (
		<div style={{ display: "flex", height: "95%", overflow: "hidden" }}>
			{/* Col 1 — Patient + plan metadata */}
			<PatientMetaPanel plan={planWithPatient} stageLogs={stageLogs} />

			{/* Col 2 — Stage list */}
			<DrawerStageList
				plan={planWithPatient}
				stageLogs={stageLogs}
				selectedLogId={selectedStageLogId}
				onSelectLog={setSelectedStageLogId}
			/>

			{/* Col 3 — Active stage content */}
			<div
				style={{
					flex: 1,
					overflowY: "auto",
					padding: "12px 16px",
					display: "flex",
					flexDirection: "column",
					gap: 12,
				}}
			>
				{activeStageLog ? (
					<InlineStageRenderer
						key={activeStageLog.id}
						plan={planWithPatient}
						activeLog={activeStageLog}
						stageLogs={stageLogs}
						activeNode={activeNode}
						joinedWatcher={
							joinedWatcherNode || joinedWatcherLog
								? { node: joinedWatcherNode, log: joinedWatcherLog }
								: undefined
						}
						onViewForm={handleViewWatcherForm}
						patientId={patientId}
						patientUuid={patientUuid}
						practitionerId={user.id}
						userId={user.id}
						onComplete={handleStageReviewed}
						onPlanChange={handleStageReviewed}
					/>
				) : (
					idleContent
				)}
			</div>
		</div>
	);

	/* ─── Modals ─────────────────────────────────────────────────────────── */
	const modals = viewAssignedForm && (
		<ReviewFormModal
			isOpen
			assignedForm={viewAssignedForm}
			onClose={() => setViewAssignedForm(null)}
			onSave={() => setViewAssignedForm(null)}
		/>
	);

	/* ─── Consult (inline) mode ──────────────────────────────────────────── */
	if (isConsult) {
		const toolbarContent = showToolbar && (
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: 12,
					padding: "8px 12px",
					borderRadius: 8,
					border: "1px solid var(--mg-border)",
				}}
			>
				<div>
					<Text strong style={{ fontSize: 14 }}>
						{plan.template?.name ?? "Treatment Plan"}
					</Text>
					{plan.template?.category && (
						<Text type="secondary" style={{ fontSize: 12, display: "block" }}>
							{plan.template.category}
							{plan.template.version && plan.template.version > 1
								? ` · v${plan.template.version}`
								: ""}
						</Text>
					)}
					<Space size={4} wrap>
						<StatusTag
							status={plan.status}
							label={
								plan.status.charAt(0).toUpperCase() + plan.status.slice(1)
							}
						/>
						{plan.current_section && (
							<ContrastTag background={plan.current_section.color}>
								{plan.current_section.name}
							</ContrastTag>
						)}
					</Space>
				</div>
				<Space>
					{plan.status === "active" && (
						<Button
							size="small"
							icon={<PauseCircleOutlined />}
							onClick={handlePause}
						>
							Pause
						</Button>
					)}
					{plan.status === "paused" && (
						<Button
							size="small"
							type="primary"
							icon={<PlayCircleOutlined />}
							onClick={handleResume}
						>
							Resume
						</Button>
					)}
					{isEditable && (
						<Popconfirm
							title="Cancel this treatment plan?"
							description="This will permanently cancel the plan for this patient and cannot be undone."
							onConfirm={handleCancel}
							okText="Cancel Plan"
							okType="danger"
							cancelText="Keep Plan"
							icon={<ExclamationCircleFilled style={{ color: "#ff4d4f" }} />}
						>
							<Button size="small" danger icon={<StopOutlined />}>
								Cancel Plan
							</Button>
						</Popconfirm>
					)}
				</Space>
			</div>
		);

		return (
			<>
				{toolbarContent}
				<BetaNotice compact />
				<div style={{ height: "calc(100% - 60px)", overflow: "hidden" }}>
					{threeColContent}
				</div>
				{modals}
			</>
		);
	}

	/* ─── Full drawer mode ───────────────────────────────────────────────── */
	const drawerHeaderActions = (
		<Space>
			{plan.status === "active" && (
				<Button size="small" icon={<PauseCircleOutlined />} onClick={handlePause}>
					Pause
				</Button>
			)}
			{plan.status === "paused" && (
				<Button
					size="small"
					type="primary"
					icon={<PlayCircleOutlined />}
					onClick={handleResume}
				>
					Resume
				</Button>
			)}
			{isEditable && (
				<Popconfirm
					title="Cancel this treatment plan?"
					description="This will permanently cancel the plan and cannot be undone."
					onConfirm={handleCancel}
					okText="Cancel Plan"
					okType="danger"
					cancelText="Keep Plan"
					icon={<ExclamationCircleFilled style={{ color: "#ff4d4f" }} />}
				>
					<Button size="small" danger icon={<StopOutlined />}>
						Cancel Plan
					</Button>
				</Popconfirm>
			)}
		</Space>
	);

	return (
		<>
			<DetailDrawer
				open={!!plan}
				onClose={onClose}
				width="85vw"
				noPadding
				title={
					patientObj
						? `${patientObj.first_name} ${patientObj.last_name}`
						: plan.patient_id
						? `Patient #${plan.patient_id}`
						: "Treatment Plan"
				}
				subtitle={
					plan.template?.name
						? `${plan.template.name}${
								plan.template?.category ? ` · ${plan.template.category}` : ""
						  }${
								plan.template?.version && plan.template.version > 1
									? ` v${plan.template.version}`
									: ""
						  }`
						: undefined
				}
				headerActions={drawerHeaderActions}
				loading={loading}
			>
				<BetaNotice />
				{threeColContent}
			</DetailDrawer>
			{modals}
		</>
	);
};

export default PlanDetailDrawer;
